// US-REPORT-01 -- Monthly family statement.

import {
  type AccountListEntry,
  type MonthlyStatement,
  accountsList,
  reportMonthlyStatement,
} from '@chorepop/core/client';
import { type Component, For, Show, createResource, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  currency: string;
  onBack: () => void;
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const MonthlyStatementScreen: Component<Props> = (props) => {
  const now = new Date();

  const [childrenRes] = createResource(async () => {
    const r = await accountsList();
    return r.ok ? r.value.filter((a) => a.roleCode === 'child') : ([] as AccountListEntry[]);
  });

  const [selectedChildId, setSelectedChildId] = createSignal<number | null>(null);
  const [year, setYear] = createSignal(now.getFullYear());
  const [month, setMonth] = createSignal(now.getMonth() + 1);

  const [statement, { refetch }] = createResource(
    () => ({ cid: selectedChildId(), y: year(), m: month() }),
    async ({ cid, y, m }) => {
      if (!cid) return null;
      const r = await reportMonthlyStatement({
        sessionId: props.sessionId,
        childAccountId: cid,
        year: y,
        month: m,
      });
      return r.ok ? r.value : null;
    },
  );

  const fmt = (minor: number) => `${props.currency} ${(minor / 100).toFixed(2)}`;

  const handlePrint = () => window.print();

  const handleShare = async () => {
    const selectedChild = (childrenRes() ?? []).find(
      (child) => child.accountId === selectedChildId(),
    );
    const message = selectedChild
      ? `${selectedChild.displayName}'s ChorePop statement for ${MONTHS[month() - 1]} ${year()}.`
      : `ChorePop family statement for ${MONTHS[month() - 1]} ${year()}.`;
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      await navigator.share({ title: 'ChorePop monthly statement', text: message });
      return;
    }
    const clipboard = (navigator as Navigator & { clipboard?: Clipboard }).clipboard;
    await clipboard?.writeText(message);
  };

  const years = () => {
    const cur = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => cur - i);
  };

  return (
    <ActionScreen
      eyebrow={`${MONTHS[month() - 1]} ${year()}`}
      title="Monthly statement"
      onBack={props.onBack}
      footer={
        <div class="statement-footer-actions">
          <button type="button" class="ghost" onClick={handlePrint}>
            Export PDF
          </button>
          <button type="button" class="primary" onClick={() => void handleShare()}>
            Share
          </button>
        </div>
      }
    >
      <div class="statement-controls action-form no-print">
        <select
          value={selectedChildId() ?? ''}
          onChange={(e) => setSelectedChildId(Number(e.currentTarget.value) || null)}
        >
          <option value="">-- select child --</option>
          <For each={childrenRes() ?? []}>
            {(c: AccountListEntry) => <option value={c.accountId}>{c.displayName}</option>}
          </For>
        </select>
        <select value={month()} onChange={(e) => setMonth(Number(e.currentTarget.value))}>
          <For each={MONTHS}>{(m, i) => <option value={i() + 1}>{m}</option>}</For>
        </select>
        <select value={year()} onChange={(e) => setYear(Number(e.currentTarget.value))}>
          <For each={years()}>{(y) => <option value={y}>{y}</option>}</For>
        </select>
        <button type="button" class="ghost" onClick={() => void refetch()}>
          View
        </button>
      </div>

      <Show when={statement.loading}>
        <p style="padding: var(--space-4);">Loading...</p>
      </Show>

      <Show when={!statement.loading && statement()}>
        {(s: () => MonthlyStatement) => (
          <div class="statement-body">
            <section class="statement-total-card">
              <span class="action-screen-eyebrow">{s().childDisplayName}, end of month</span>
              <div>
                <strong>{fmt(s().closingBalanceMinorUnits)}</strong>
                <span
                  class={
                    s().closingBalanceMinorUnits >= s().openingBalanceMinorUnits
                      ? 'is-positive'
                      : 'is-negative'
                  }
                >
                  {s().closingBalanceMinorUnits >= s().openingBalanceMinorUnits ? '+' : ''}
                  {fmt(s().closingBalanceMinorUnits - s().openingBalanceMinorUnits)} this month
                </span>
              </div>
            </section>

            <section class="statement-metric-grid" aria-label="Monthly totals">
              <div>
                <span>Earned</span>
                <strong>{fmt(s().totalDeposited)}</strong>
              </div>
              <div>
                <span>Spent</span>
                <strong>{fmt(s().totalWithdrawn)}</strong>
              </div>
              <div>
                <span>Moved</span>
                <strong>{fmt(s().totalTransferred)}</strong>
              </div>
              <div>
                <span>Opening</span>
                <strong>{fmt(s().openingBalanceMinorUnits)}</strong>
              </div>
            </section>

            {/* Per-bucket breakdown */}
            <div class="statement-bucket-section">
              <span class="action-screen-eyebrow">By bucket</span>
              <table class="statement-table">
                <thead>
                  <tr>
                    <th>Bucket</th>
                    <th class="statement-amount">Opening</th>
                    <th class="statement-amount">Deposited</th>
                    <th class="statement-amount">Withdrawn</th>
                    <th class="statement-amount">Closing</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={s().buckets}>
                    {(b) => (
                      <tr>
                        <td>{b.bucketName}</td>
                        <td class="statement-amount">{fmt(b.openingMinorUnits)}</td>
                        <td class="statement-amount">+ {fmt(b.totalDeposited)}</td>
                        <td class="statement-amount">- {fmt(b.totalWithdrawn)}</td>
                        <td class="statement-amount">{fmt(b.closingMinorUnits)}</td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>

            <p class="statement-footer">
              Generated by ChorePop -- {s().periodLabel} -- {s().childDisplayName}
            </p>
          </div>
        )}
      </Show>

      <Show when={!statement.loading && !statement() && selectedChildId()}>
        <p style="padding: var(--space-4); color: var(--ink-2);">No data for this period.</p>
      </Show>
    </ActionScreen>
  );
};
